// styled components
import {Actions, Header} from './style';
import styled from 'styled-components/macro';

// components
import Logo from '@ui/Logo';
import MenuButton from '@ui/MenuButton';
import ShapeButton from '@ui/ShapeButton';
import {motion} from 'framer-motion';
import CurrentUser from '@layout/Panel/CurrentUser';
import ClinicSwitcher from '@components/ClinicSwitcher';
import {ModalContent} from '@components/ModalWindow';
import Draggable from 'react-draggable';
import Btn from '@ui/Btn';

// hooks
import useWindowSize from '@hooks/useWindowSize';
import usePanelScroll from '@hooks/usePanelScroll';
import {useSidebarContext} from '@contexts/sidebarContext';
import {useRef, useEffect, useMemo, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {useAuth} from '@hooks/useAuth';
import {authService} from '@services/auth.service';
import {notificationService} from '@services/agenda.service';
import {getInventoryListResult, inventoryService} from '@services/inventory.service';
import {getLowStockInventoryItems} from '@utils/inventoryAlerts';
import {portalClienteService} from '../../apps/client-portal/services/portal-cliente.service';
import {textSizes, colors, fonts} from '@styles/vars';

const LOCAL_STOCK_NOTIFICATION_READ_KEY = 'inventory_low_stock_notification_read_ids';

const getStoredReadIds = () => {
    try {
        const raw = localStorage.getItem(LOCAL_STOCK_NOTIFICATION_READ_KEY);
        const parsed = raw ? JSON.parse(raw) : [];
        return Array.isArray(parsed) ? parsed.map(String) : [];
    } catch (error) {
        return [];
    }
};

const storeReadIds = ids => {
    localStorage.setItem(LOCAL_STOCK_NOTIFICATION_READ_KEY, JSON.stringify([...new Set(ids.map(String))]));
};

const buildLowStockNotifications = (inventoryRows = []) => {
    const readIds = new Set(getStoredReadIds());
    const lowStockItems = getLowStockInventoryItems(inventoryRows);

    return lowStockItems.map(item => {
        const notificationId = `inventory-low-stock-${String(item.id)}`;
        return {
            id: notificationId,
            titulo: 'Stock bajo detectado',
            mensaje: `${item.nombre || item.modelo || 'Producto'} tiene ${item.cantidad} unidades (limite ${item.reorderLimit}).`,
            tipo: 'Inventario',
            source: 'local-inventory',
            read: readIds.has(notificationId),
            leida: readIds.has(notificationId),
            is_read: readIds.has(notificationId)
        };
    });
};

const NotificationPanel = styled(ModalContent)`
    max-width: 560px;
    width: 100%;
    ${props => props.$minimized ? 'height: 60px; overflow: hidden;' : ''}
`;

const NotificationDraggableWrapper = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    max-width: 560px;
    outline: none;
    z-index: 2000001;
`;

const NotificationHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;

`;

const NotificationDragHandle = styled.div`
    flex: 1;
    min-width: 0;
    display: flex;
    align-items: center;
    gap: 10px;
    cursor: grab;
    user-select: none;

    &:active {
        cursor: grabbing;
    }

    .notification-drag-hint {
        flex-shrink: 0;
        font-size: 18px;
        line-height: 1;
        opacity: 0.55;
        color: ${colors.gray};
    }

    &:hover .notification-drag-hint {
        opacity: 0.9;
    }

    h3 {
        flex: 1;
        min-width: 0;
        margin: 0;
    }
`;

const NotificationToolbar = styled.div`
    display: flex;
    gap: 8px;
    flex-shrink: 0;
    align-items: center;
`;

const NotificationList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    max-height: 420px;
    overflow: auto;
`;

const EmpresaContext = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 10px;
    padding: 8px 14px;
    border-radius: 10px;
    background: rgba(38, 98, 240, 0.08);
    border: 1px solid rgba(38, 98, 240, 0.16);
    font-family: ${fonts.body};
    font-size: ${textSizes['13']};
    font-weight: 600;
    color: ${colors.blue};
    max-width: 280px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    .icon {
        color: ${colors.blue};
        flex-shrink: 0;
    }
`;

const NotificationItem = styled.button`
    width: 100%;
    text-align: left;
    border-radius: 10px;
    padding: 12px 14px;
    background: ${props => props.$read ? 'rgba(144, 161, 172, 0.12)' : 'rgba(38, 98, 240, 0.08)'};
    border: 1px solid ${props => props.$read ? 'transparent' : 'rgba(38, 98, 240, 0.18)'};
    display: flex;
    flex-direction: column;
    gap: 4px;

    .title {
        font-size: ${textSizes['14']};
        font-weight: 600;
        color: inherit;
    }

    .body {
        font-size: ${textSizes['12']};
        opacity: 0.85;
    }

    .meta {
        font-size: ${textSizes['10']};
        color: ${colors.gray};
        text-transform: uppercase;
    }
`;

const Panel = () => {
    const navigate = useNavigate();
    const {user, tenantName} = useAuth();
    const {width} = useWindowSize();
    const isMobile = width < 768;
    const isDesktop = width >= 1366;
    const classname = usePanelScroll();
    const {isSidebarOpen} = useSidebarContext();
    const headerRef = useRef(null);
    const notificationsDragRef = useRef(null);
    const [notificationsOpen, setNotificationsOpen] = useState(false);
    const [notificationsMinimized, setNotificationsMinimized] = useState(false);
    const [notificationsPosition, setNotificationsPosition] = useState({x: window.innerWidth - 600, y: 100});
    const [notifications, setNotifications] = useState([]);
    const isPortalShell = authService.shouldUseClientPortalShell();
    const empresaNombre = user?.empresa_nombre || tenantName || 'Portal de clientes';

    const unreadCount = useMemo(
        () => notifications.filter(item => !(item?.read || item?.leida || item?.is_read)).length,
        [notifications]
    );

    const mapPortalNotifications = items =>
        (Array.isArray(items) ? items : []).map(item => ({
            id: item.id,
            titulo: item.titulo,
            title: item.titulo,
            mensaje: item.mensaje,
            message: item.mensaje,
            tipo: item.severidad,
            type: item.severidad,
            read: Boolean(item.leida),
            leida: Boolean(item.leida),
            is_read: Boolean(item.leida),
            source: 'portal',
            accion_sugerida: item.accion_sugerida ?? null,
            fecha_generacion: item.fecha_generacion
        }));

    useEffect(() => {
        if (!isPortalShell) {
            return undefined;
        }

        let mounted = true;

        const loadPortalNotifications = async () => {
            try {
                const response = await portalClienteService.getNotificaciones({limit: 50});

                if (!mounted) {
                    return;
                }

                setNotifications(mapPortalNotifications(response.data));
            } catch {
                if (mounted) {
                    setNotifications([]);
                }
            }
        };

        loadPortalNotifications();
        const timer = setInterval(loadPortalNotifications, 30000);

        return () => {
            mounted = false;
            clearInterval(timer);
        };
    }, [isPortalShell, notificationsOpen]);

    useEffect(() => {
        const handleOpenPortalNotifications = () => {
            if (authService.shouldUseClientPortalShell()) {
                setNotificationsOpen(true);
            }
        };

        window.addEventListener('open-portal-notifications', handleOpenPortalNotifications);

        return () => {
            window.removeEventListener('open-portal-notifications', handleOpenPortalNotifications);
        };
    }, []);

    useEffect(() => {
        if (isPortalShell) {
            return undefined;
        }

        let mounted = true;

        const loadNotifications = async () => {
            const [apiNotificationsResult, inventoryResult] = await Promise.allSettled([
                notificationService.getMine(),
                inventoryService.getAll()
            ]);

            if (!mounted) {
                return;
            }

            const apiNotifications = apiNotificationsResult.status === 'fulfilled' && Array.isArray(apiNotificationsResult.value)
                ? apiNotificationsResult.value
                : [];

            const inventoryRows = inventoryResult.status === 'fulfilled'
                ? getInventoryListResult(inventoryResult.value).data
                : [];

            const stockNotifications = buildLowStockNotifications(inventoryRows);
            setNotifications([...stockNotifications, ...apiNotifications]);
        };

        loadNotifications();
        const timer = setInterval(loadNotifications, 30000);

        return () => {
            mounted = false;
            clearInterval(timer);
        };
    }, [isPortalShell]);

    const handleReadOne = async notificationId => {
        const notification = notifications.find(item => String(item.id) === String(notificationId));

        if (notification?.source === 'local-inventory') {
            const nextReadIds = [...getStoredReadIds(), String(notificationId)];
            storeReadIds(nextReadIds);
            setNotifications(prev => prev.map(item =>
                String(item.id) === String(notificationId)
                    ? {...item, read: true, leida: true, is_read: true}
                    : item
            ));
            return;
        }

        if (notification?.source === 'portal') {
            try {
                await portalClienteService.markNotificacionLeida(String(notificationId));
                setNotifications(prev => prev.map(item =>
                    String(item.id) === String(notificationId)
                        ? {...item, read: true, leida: true, is_read: true}
                        : item
                ));

                if (notification?.accion_sugerida?.ruta) {
                    setNotificationsOpen(false);
                    navigate(notification.accion_sugerida.ruta);
                }
            } catch (error) {
                // keep UI silent; user can retry
            }

            return;
        }

        try {
            await notificationService.markRead(notificationId);
            setNotifications(prev => prev.map(item =>
                String(item.id) === String(notificationId)
                    ? {...item, read: true, leida: true, is_read: true}
                    : item
            ));
        } catch (error) {
            // keep UI silent; user can retry
        }
    };

    const handleReadAll = async () => {
        if (isPortalShell) {
            const unreadPortalNotifications = notifications.filter(
                item => item?.source === 'portal' && !(item?.read || item?.leida || item?.is_read)
            );

            await Promise.allSettled(
                unreadPortalNotifications.map(item =>
                    portalClienteService.markNotificacionLeida(String(item.id))
                )
            );

            setNotifications(prev => prev.map(item => ({...item, read: true, leida: true, is_read: true})));
            return;
        }

        const localNotifications = notifications.filter(item => item?.source === 'local-inventory');
        if (localNotifications.length) {
            const nextReadIds = [
                ...getStoredReadIds(),
                ...localNotifications.map(item => String(item.id))
            ];
            storeReadIds(nextReadIds);
        }

        try {
            await notificationService.markAllRead();
            setNotifications(prev => prev.map(item => ({...item, read: true, leida: true, is_read: true})));
        } catch (error) {
            if (localNotifications.length) {
                setNotifications(prev => prev.map(item =>
                    item?.source === 'local-inventory'
                        ? {...item, read: true, leida: true, is_read: true}
                        : item
                ));
            }
        }
    };

    const handleDragStop = (e, data) => {
        if (!data || typeof data.x !== 'number' || typeof data.y !== 'number') {
            return;
        }
        setNotificationsPosition({x: data.x, y: data.y});
    };

    const handleMinimize = () => {
        setNotificationsMinimized(prev => !prev);
    };

    useEffect(() => {
        if (!headerRef.current) {
            return;
        }
        document.documentElement.style.setProperty('--header-height', `${headerRef.current.offsetHeight}px`);
    }, [width]);

    return (
        <Header as={motion.header}
                animate={{y: isSidebarOpen && isMobile ? '-100%' : 0}}
                transition={{duration: .3, ease: 'linear', type: 'tween'}}
                className={classname}
                ref={headerRef}>
            {
                !isDesktop && (
                    <div className="logo-wrapper">
                        <Logo compact={isMobile}/>
                    </div>
                )
            }
            {
                isMobile ?
                    <MenuButton/>
                    :
                    <Actions>
                        {isPortalShell ? (
                            <EmpresaContext title={empresaNombre}>
                                <i className="icon icon-users" aria-hidden="true"></i>
                                <span>{empresaNombre}</span>
                            </EmpresaContext>
                        ) : (
                            <ClinicSwitcher/>
                        )}
                        <ShapeButton
                            shape="square"
                            label="Notificaciones"
                            icon="bell"
                            hasNotification={unreadCount > 0}
                            handler={() => setNotificationsOpen(true)}
                        />
                        {
                            isDesktop ?
                                <CurrentUser/> : <ShapeButton shape="square" label="Profile" icon="user"/>
                        }
                        { width < 1366 && <MenuButton/> }
                    </Actions>
            }

            {notificationsOpen && (
                <NotificationDraggableWrapper>
                    <Draggable
                        nodeRef={notificationsDragRef}
                        handle=".notification-drag-handle"
                        defaultPosition={notificationsPosition}
                        onStop={handleDragStop}
                    >
                        <div ref={notificationsDragRef}>
                            <NotificationPanel $minimized={notificationsMinimized}>
                                <NotificationHeader className="notification-header">
                                    <NotificationDragHandle className="notification-drag-handle" title="Arrastrar ventana">
                                        <i className="icon icon-dots notification-drag-hint" aria-hidden="true"/>
                                        <h3>Notificaciones</h3>
                                    </NotificationDragHandle>
                                    <NotificationToolbar className="notification-toolbar">
                                        {!notificationsMinimized && <Btn text="Marcar todas" handler={handleReadAll} />}
                                        <Btn text={notificationsMinimized ? 'Restaurar' : 'Minimizar'} handler={handleMinimize} />
                                        <Btn text="Cerrar" variant="secondary" handler={() => setNotificationsOpen(false)} />
                                    </NotificationToolbar>
                                </NotificationHeader>

                                {!notificationsMinimized && (
                                    <NotificationList>
                                        {notifications.length ? notifications.map(item => (
                                            <NotificationItem
                                                key={item.id}
                                                type="button"
                                                $read={item?.read || item?.leida || item?.is_read}
                                                onClick={() => handleReadOne(item.id)}
                                            >
                                                <span className="title">{item?.titulo || item?.title || 'Notificacion'}</span>
                                                <span className="body">{item?.mensaje || item?.message || item?.descripcion || 'Sin detalle'}</span>
                                                <span className="meta">{item?.tipo || item?.type || 'Sistema'}</span>
                                            </NotificationItem>
                                        )) : (
                                            <p style={{margin: 0}}>No hay notificaciones.</p>
                                        )}
                                    </NotificationList>
                                )}
                            </NotificationPanel>
                        </div>
                    </Draggable>
                </NotificationDraggableWrapper>
            )}
        </Header>
    )
}

export default Panel;